import './DocumentUploadInput.less';

import { useReframe, dispatch } from 'nike-re-framejs';
import clsx from 'clsx';
import React, { ChangeEvent, ForwardedRef, useCallback, useEffect, useRef, useState } from 'react';
import PlusInCircleIcon from '../../icons/PlusInCircleIcon';
import CheckIcon from '../../icons/CheckIcon';
import i18n from '../../common/i18n';
import PDFIcon from '../../icons/PDFIcon';
import { documentUpload, getUploadError, getUploadProgress } from './h&s';
import { Map } from 'immutable';
import PlusIcon from '../../icons/PlusIcon';
import AttachmentIcon from '../../icons/svg/attachment-icon-sv.svg';
import { NegativeInvIcon as DeleteDocumentIcon } from '../../icons/NegativeInvIcon';
import SelfieIcon from '../../icons/SelfieIcon';

function DocumentsUploader({ slot, selectedDocument }) {
    if (!selectedDocument) {
        return null;
    }
    const idDocDms = selectedDocument.get('idDocDms');
    const docType = selectedDocument.get('docType');
    const idSlot = selectedDocument.get('idSlot');
    const clientHash = selectedDocument.get('clientHash');
    const slotDescription = { slot, idSlot, idDocDms, clientHash };
    const placeHolderText =
        docType === 'SELFIE'
            ? 'nui.documentUpload.selfie'
            : selectedDocument.get('pages') > 1
            ? 'nui.documentUpload.frontPage'
            : 'nui.documentUpload.fotoPage';

    return (
        <>
            <DocumentUploadInput
                slotDescription={Object.assign({ page: 1 }, slotDescription)}
                placeHolderText={i18n(placeHolderText)}
                dataAtid="rc-docs-upload-front-page"
                style={docType === 'SELFIE' ? 'SELFIE' : 'CARD'}
            />
            {selectedDocument.get('pages') > 1 && (
                <DocumentUploadInput
                    slotDescription={Object.assign({ page: 2 }, slotDescription)}
                    placeHolderText={i18n('nui.documentUpload.backPage')}
                    dataAtid="rc-docs-upload-back-page"
                />
            )}
        </>
    );
}

type UploadInputStyle = 'CARD' | 'STRIP' | 'SELFIE';

export function DocumentUploadInput({
    slotDescription,
    placeHolderText,
    style = 'CARD',
    beforeUpload,
    dataAtid,
    formCompleted,
    deleteIcon,
    removeUploadFile
}: {
    slotDescription: { slot: number; idSlot: number; idDocDms: number; page: number; clientHash: string };
    placeHolderText: string;
    style?: UploadInputStyle;
    beforeUpload?: () => void;
    dataAtid?: string;
    formCompleted?: boolean;
    deleteIcon?: boolean;
    removeUploadFile?: () => void;
}) {
    const derefSub = useReframe();
    const uploadError = derefSub(getUploadError(slotDescription.slot, slotDescription.idDocDms, slotDescription.page));
    let progress =
        derefSub(getUploadProgress(slotDescription.slot, slotDescription.idDocDms, slotDescription.page)) || Map();

    let progressPercent = progress.get('progress', 0);
    let done = progress.get('done');

    if (uploadError) {
        progressPercent = 0;
        done = false;
    }
    console.log('progressPercent', progressPercent);
    const dropbox = useRef<HTMLDivElement>();
    const dropFnRef = useRef<(DragEvent) => void>();
    const [dragging, setDragging] = useState(false);
    const inputRef = useRef();

    useEffect(() => {
        // Everything around if statement
        const dropElement = dropbox.current;
        if (dropElement) {
            dropElement.addEventListener('dragenter', dragenter, false);
            dropElement.addEventListener('dragover', dragover, false);
            dropElement.addEventListener('dragleave', dragleave, false);
            dropElement.addEventListener('drop', e => dropFnRef.current(e) /* drop */, false);

            return () => {
                dropElement.removeEventListener('dragenter', dragenter, false);
                dropElement.removeEventListener('dragover', dragover, false);
                dropElement.removeEventListener('dragleave', dragleave, false);
                dropElement.removeEventListener('drop', e => dropFnRef.current(e) /* drop */, false);
            };
        }
    }, []);

    function dragenter(e) {
        e.stopPropagation();
        e.preventDefault();
    }

    function dragleave(e) {
        e.stopPropagation();
        e.preventDefault();
        setDragging(false);
    }

    function dragover(e) {
        e.stopPropagation();
        e.preventDefault();
        setDragging(true);
    }

    // what a hack
    dropFnRef.current = function drop(e) {
        e.stopPropagation();
        e.preventDefault();
        setDragging(false);
        const dt = e.dataTransfer;
        const files = dt.files;
        onFileChange(files[0]);
    };

    const onFileChange = e => {
        const file = e.target.files[0];
        if (file) {
            beforeUpload && beforeUpload();
            dispatch(documentUpload(slotDescription, file));
        }
        e.target.value = '';
    };

    const clear = e => {
        e.stopPropagation();
        e.preventDefault();
        removeUploadFile();
        // @ts-ignore
        inputRef.current.value = null;
    };

    return (
        <div className="document-upload" data-atid={dataAtid}>
            <div
                ref={dropbox}
                className={clsx('document-upload-input', {
                    loading: !done && progressPercent > 0,
                    dragging,
                    loaded: done,
                    error: uploadError,
                    strip: style === 'STRIP',
                    card: style === 'CARD',
                    selfie: style === 'SELFIE'
                })}
            >
                {done && !formCompleted ? (
                    <>
                        <div
                            className={clsx('document-upload-input-preview', {
                                pdf: progress.get('resolvedType') === 'pdf'
                            })}
                            data-atid={'docs-upload-success'}
                        >
                            {progress.get('resolvedType') === 'image' && (
                                <img
                                    className="file-input-preview-img"
                                    src={progress.get('imageUrl')}
                                    alt="Náhľad dokumentu"
                                />
                            )}
                            {progress.get('resolvedType') === 'pdf' ? (
                                <div className="file-input-preview-pdf">
                                    <PDFIcon />
                                </div>
                            ) : null}
                            <div className="document-upload-input-preview-icon">
                                <CheckIcon />
                            </div>
                        </div>
                        {deleteIcon ? <DeleteUploadedDocument clear={clear} /> : null}
                    </>
                ) : (
                    <>
                        <div className="filler" style={{ width: progressPercent + '%' }} />
                        <div className="document-upload-input-placeholder">
                            {style === 'SELFIE' && (
                                <div>
                                    <SelfieIcon />
                                </div>
                            )}
                            {style !== 'STRIP' && <PlusInCircleIcon />}
                            <div className="flex-center">
                                {style === 'STRIP' && <PlusIcon />}
                                {!done && progressPercent > 0
                                    ? i18n('nui.registration.common.loading')
                                    : placeHolderText}
                            </div>
                        </div>
                    </>
                )}

                <input
                    type="file"
                    name="myImage1"
                    accept=".png,.jpg,.jpeg,.pdf"
                    onChange={onFileChange}
                    data-atid="rc-docs-upload-input"
                    disabled={formCompleted}
                    ref={inputRef}
                />
            </div>
            {uploadError ? <div className="nui-label-error fs-12 pb-5">{uploadError}</div> : null}
        </div>
    );
}

const DeleteUploadedDocument = ({ clear }) => (
    <div className="document-upload-input-remove p-abs pointer" onClick={clear}>
        <DeleteDocumentIcon />
    </div>
);

type DirectFileInputProps = {
    style: UploadInputStyle;
    placeholderText: string;
    formCompleted?: boolean;
};
export const DirectFileUploadInput = React.forwardRef(
    ({ style, placeholderText, formCompleted }: DirectFileInputProps, ref: ForwardedRef<File>) => {
        const [name, setName] = useState<string>();
        const [type, setType] = useState<string>();
        const inputRef = useRef();
        const onFileChange = (e: ChangeEvent<HTMLInputElement>) => {
            const file = e.target.files[0];
            // @ts-ignore
            ref.current = file;
            setName(file?.name);
            setType(file?.type);
        };
        const clear = useCallback(
            e => {
                e.stopPropagation();
                e.preventDefault();
                // @ts-ignore
                inputRef.current.value = null;
                // @ts-ignore
                ref.current = null;
                setName(null);
                setType(null);
            },
            [ref]
        );
        return (
            <div className="document-upload">
                <div
                    className={clsx('document-upload-input', {
                        strip: style === 'STRIP',
                        card: style === 'CARD',
                        loaded: !!name
                    })}
                >
                    {name && !formCompleted ? (
                        <>
                            <div
                                className={clsx('document-upload-input-preview ellipsis mx-20', {
                                    pdf: type === 'pdf'
                                })}
                                data-atid={'docs-upload-success'}
                            >
                                <div className="mr-10">
                                    <AttachmentIcon />
                                </div>
                                <div className={'ellipsis'}>{name}</div>
                            </div>
                            <DeleteUploadedDocument clear={clear} />
                        </>
                    ) : (
                        <>
                            <div className="document-upload-input-placeholder">
                                {style === 'CARD' && <PlusInCircleIcon />}
                                <div className="flex-center">
                                    {style === 'STRIP' && <PlusIcon />}
                                    {placeholderText}
                                </div>
                            </div>
                        </>
                    )}
                    <input
                        type="file"
                        accept=".png,.jpg,.jpeg,.pdf"
                        multiple={false}
                        onChange={onFileChange}
                        disabled={formCompleted}
                        ref={inputRef}
                    />
                </div>
            </div>
        );
    }
);

export default DocumentsUploader;
